if (!customElements.get('tt-size-picker')) {
  customElements.define('tt-size-picker', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true, signal: this.lifecycle.signal });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.ready = false;
    }

    async init() {
      await Promise.all(['product-form', 'variant-radios'].map(name => customElements.whenDefined(name)));
      if (this.ready || !this.isConnected || this.lifecycle.signal.aborted) return;
      this.host = this.closest('variant-radios');
      this.info = this.closest('product-info');
      this.fieldset = this.closest('fieldset');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.host || !this.info || !this.form) return;
      this.variants = JSON.parse(this.host.querySelector('script[type="application/json"]').textContent);
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.fieldsets = Array.from(this.host.children).filter(element => element.tagName === 'FIELDSET');
      this.radios = Array.from(this.fieldset.querySelectorAll('input[type="radio"]'));
      this.trigger = this.querySelector('.tt-size-picker__trigger');
      this.list = this.querySelector('[role="listbox"]');
      this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
      this.label = this.querySelector('[data-size-label]');
      this.notice = this.querySelector('.tt-size-picker__notice');
      this.confirmedSize = '';
      const signal = this.lifecycle.signal;
      this.addEventListener('click', event => {
        const option = event.target.closest('[data-size-value]');
        if (option) {
          this.choose(option);
        } else if (event.target.closest('.tt-size-picker__trigger')) {
          this.list.hidden ? this.open() : this.close();
        }
      }, { signal });
      this.addEventListener('keydown', event => this.onKeyDown(event), { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.close(false);
      }, { signal });
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.close(false);
      }, { signal });
      this.host.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"]')) return;
        const previous = this.confirmedSize;
        this.sync();
        if (previous && !this.confirmedSize) this.showNotice(this.dataset.changedMessage);
      }, { capture: true, signal });
      this.ready = true;
      this.hidden = false;
      this.fieldset.classList.add('tt-size-field-ready');
      this.info.classList.add('tt-compact-buy');
      const changed = this.sync();
      if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
    }

    matchingVariants() {
      const selected = this.fieldsets.map(fieldset => fieldset.querySelector('input[type="radio"]:checked')?.value);
      return this.variants.filter(variant => variant.options.every((value, index) => index === this.sizeIndex || value === selected[index]));
    }

    sync() {
      const available = this.matchingVariants().filter(variant => variant.available);
      this.available = available;
      if (!available.some(variant => variant.options[this.sizeIndex] === this.confirmedSize)) this.confirmedSize = '';
      const checked = this.radios.find(radio => radio.checked);
      let changed = null;
      if (available.length && !available.some(variant => variant.options[this.sizeIndex] === checked?.value)) {
        changed = this.radios.find(radio => radio.value === available[0].options[this.sizeIndex]);
        if (changed) changed.checked = true;
      }
      this.options.forEach(option => {
        const value = option.dataset.sizeValue;
        const enabled = available.some(variant => variant.options[this.sizeIndex] === value);
        option.disabled = !enabled;
        option.setAttribute('aria-disabled', String(!enabled));
        option.setAttribute('aria-selected', String(value === this.confirmedSize));
        option.querySelector('[data-size-stock]').hidden = enabled;
      });
      this.label.textContent = this.confirmedSize ? `${this.dataset.selectedLabel} ${this.confirmedSize}` : this.dataset.placeholder;
      this.dataset.selected = String(Boolean(this.confirmedSize));
      this.info.classList.toggle('tt-size-unavailable', available.length === 0);
      return changed;
    }

    choose(option) {
      if (option.disabled || !this.available.some(variant => variant.options[this.sizeIndex] === option.dataset.sizeValue)) return;
      const radio = this.radios.find(input => input.value === option.dataset.sizeValue);
      if (!radio) return;
      this.confirmedSize = radio.value;
      this.notice.hidden = true;
      this.trigger.removeAttribute('aria-invalid');
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
    }

    validateSelection() {
      if (!this.ready) return false;
      const variant = this.matchingVariants().find(item => item.available && item.options[this.sizeIndex] === this.confirmedSize);
      const formVariantId = this.form.querySelector('[name="id"]')?.value;
      if (variant && String(variant.id) === formVariantId) return true;
      this.confirmedSize = '';
      this.sync();
      this.showNotice(this.dataset.requiredMessage);
      this.trigger.setAttribute('aria-invalid', 'true');
      this.open();
      this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return false;
    }

    showNotice(message) {
      this.notice.textContent = message;
      this.notice.hidden = false;
    }

    open(last = false) {
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      const enabled = this.options.filter(option => !option.disabled);
      const selected = enabled.find(option => option.dataset.sizeValue === this.confirmedSize);
      (selected || (last ? enabled.at(-1) : enabled[0]) || this.trigger).focus({ preventScroll: true });
    }

    close(focus = true) {
      if (!this.list || this.list.hidden) return;
      this.list.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (focus) this.trigger.focus({ preventScroll: true });
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (this.list.hidden) return this.open(event.key === 'ArrowUp' || event.key === 'End');
      const enabled = this.options.filter(option => !option.disabled);
      if (!enabled.length) return;
      let index = enabled.indexOf(document.activeElement);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = enabled.length - 1;
      else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      enabled[index].focus();
    }
  });
}
